export const MAX_IMAGE_UPLOAD_BYTES = 50 * 1024 * 1024;

export async function convertImageFileToWebpDataUrl(file, options = {}) {
  if (!file) {
    throw new Error('Please select an image file.');
  }

  const maxBytes = options.maxBytes || MAX_IMAGE_UPLOAD_BYTES;

  if (file.size > maxBytes) {
    throw new Error(`Image must be ${formatFileSize(maxBytes)} or smaller.`);
  }

  const image = await loadImageFromFile(file);
  const canvas = document.createElement('canvas');
  canvas.width = image.naturalWidth || image.width;
  canvas.height = image.naturalHeight || image.height;

  if (!canvas.width || !canvas.height) {
    throw new Error('Selected image could not be read.');
  }

  const context = canvas.getContext('2d');

  if (!context) {
    throw new Error('Image conversion is not available in this browser.');
  }

  context.drawImage(image, 0, 0, canvas.width, canvas.height);

  const webpBlob = await canvasToWebpBlob(canvas, options.quality ?? 0.9);
  const dataUrl = await blobToDataUrl(webpBlob);

  return {
    dataUrl,
    mimeType: webpBlob.type || 'image/webp',
    originalSize: file.size,
    convertedSize: webpBlob.size,
  };
}

function loadImageFromFile(file) {
  return new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file);
    const image = new Image();

    image.onload = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(image);
    };
    image.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error('Please upload a valid image file.'));
    };
    image.src = objectUrl;
  });
}

function canvasToWebpBlob(canvas, quality) {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error('Unable to convert image to WEBP.'));
          return;
        }

        if (blob.type !== 'image/webp') {
          reject(new Error('WEBP conversion is not available in this browser.'));
          return;
        }

        resolve(blob);
      },
      'image/webp',
      quality
    );
  });
}

function blobToDataUrl(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(new Error('Unable to read converted image.'));
    reader.readAsDataURL(blob);
  });
}

function formatFileSize(bytes) {
  return `${Math.round(bytes / 1024 / 1024)}MB`;
}
